// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { useContext } from 'react';
import { DEFAULT_THEME_STYLE, ThemeContext } from '@/contexts/theme';

export interface LiquidGlassTheme {
  themeStyle: string;
  resolvedTheme: 'light' | 'dark';
  /** Lens 材质：折射 / 模糊 / 色散（0–1）。 */
  material: { refraction: number; blur: number; dispersion: number };
  /** Lens 场景（artwork 回调会按主题重绘品牌壳渐变）。 */
  scene: 'aurora' | 'sunset' | 'ocean' | 'grid';
  /** Lattice 世界：暗色主题走夜世界，浅色走纸世界。 */
  world: 'night' | 'paper';
}

/**
 * 批次 8 M6：八套主题共用同一套液态玻璃引擎，材质按主题调参——
 * glass 是通透厚玻璃，paper 是磨砂纸感（高模糊低折射无色散），
 * 其余主题介于两者之间；都仍由同一 WebGL2 折射渲染器绘制。
 */
const MATERIAL_BY_STYLE: Record<string, { material: LiquidGlassTheme['material']; scene: LiquidGlassTheme['scene'] }> = {
  glass: { material: { refraction: 0.62, blur: 0.2, dispersion: 0.3 }, scene: 'aurora' },
  paper: { material: { refraction: 0.18, blur: 0.62, dispersion: 0.04 }, scene: 'grid' },
  midnight: { material: { refraction: 0.5, blur: 0.34, dispersion: 0.2 }, scene: 'sunset' },
  'midnight-contrast': { material: { refraction: 0.44, blur: 0.4, dispersion: 0.16 }, scene: 'sunset' },
  ocean: { material: { refraction: 0.54, blur: 0.3, dispersion: 0.22 }, scene: 'ocean' },
  forest: { material: { refraction: 0.46, blur: 0.36, dispersion: 0.18 }, scene: 'ocean' },
  morandi: { material: { refraction: 0.34, blur: 0.48, dispersion: 0.12 }, scene: 'grid' },
};

const FALLBACK = MATERIAL_BY_STYLE.glass;

/**
 * 安全读取主题：ThemeProvider 缺失时（单测 / 独立挂载）回退 DOM 上的主题标记，
 * 而不是抛错——玻璃层是增强，绝不能影响页面可用性。
 */
export function useLiquidGlassTheme(): LiquidGlassTheme {
  const ctx = useContext(ThemeContext);
  const root = typeof document === 'undefined' ? null : document.documentElement;
  const themeStyle = ctx?.themeStyle ?? root?.getAttribute('data-theme-style') ?? DEFAULT_THEME_STYLE;
  const resolvedTheme = ctx?.resolvedTheme ?? (root?.classList.contains('dark') ? 'dark' : 'light');
  const preset = MATERIAL_BY_STYLE[themeStyle] ?? FALLBACK;
  return {
    themeStyle,
    resolvedTheme,
    material: preset.material,
    scene: preset.scene,
    world: resolvedTheme === 'dark' ? 'night' : 'paper',
  };
}
